import * as React from 'react';
import ReactDOM from 'react-dom';
import { TriggerType, usePopperTooltip } from 'react-popper-tooltip';
import 'react-popper-tooltip/dist/styles.css';
import styled from 'styled-components/macro';

export interface TooltipProps {
  children: React.ReactNode;
  content?: React.ReactNode;
  trigger?: 'focus' | 'click' | 'hover' | 'right-click' | 'double-click' | TriggerType[] | null | undefined;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'auto';
  visible?: boolean;
  onVisibleChange?: (visible: boolean) => void;
  closeOnOutsideClick?: boolean;
  className?: string;
  arrowClassName?: string;
  maxWidth?: string | number;
  delayShow?: number;
}

const TooltipContainer = styled.div<{ $maxWidth?: string | number }>`
  max-width: ${({ $maxWidth }) => ($maxWidth ? $maxWidth : 'auto')};
  color: ${({ theme }) =>
    theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey10 : theme.colorPalette.grey.grey9};
  word-wrap: break-word;
`;
const TooltipArrow = styled.div``;

export function TooltipControlled({
  children,
  content = '',
  trigger = 'hover',
  placement = 'top',
  visible,
  onVisibleChange,
  closeOnOutsideClick = true,
  className = 'tooltip-container',
  arrowClassName = 'tooltip-arrow',
  maxWidth,
  delayShow = 0,
}: TooltipProps) {
  const [delayedVisible, setDelayedVisible] = React.useState(false);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    getArrowProps,
    getTooltipProps,
    setTooltipRef,
    setTriggerRef,
    visible: internalVisible,
  } = usePopperTooltip({
    trigger,
    closeOnOutsideClick,
    visible: delayShow > 0 ? delayedVisible : visible,
    onVisibleChange: (newVisible) => {
      if (delayShow > 0) {
        if (newVisible) {
          // Clear any existing timeout
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          // Set a new timeout to show the tooltip after delay
          timeoutRef.current = setTimeout(() => {
            setDelayedVisible(true);
            onVisibleChange?.(true);
          }, delayShow);
        } else {
          // Clear timeout and hide immediately
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          setDelayedVisible(false);
          onVisibleChange?.(false);
        }
      } else {
        onVisibleChange?.(newVisible);
      }
    },
    placement,
  });

  const isVisible = delayShow > 0 ? delayedVisible : visible !== undefined ? visible : internalVisible;
  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <div ref={setTriggerRef}>{children}</div>

      {isVisible &&
        ReactDOM.createPortal(
          <TooltipContainer ref={setTooltipRef} {...getTooltipProps({ className })} $maxWidth={maxWidth}>
            {content}
            <TooltipArrow {...getArrowProps({ className: arrowClassName })} />
          </TooltipContainer>,
          document.body,
        )}
    </>
  );
}

export default TooltipControlled;
